import React, { Component } from "react";
import styled from "styled-components";
import {
    MenuItem,
    ListItem,
    ListSubheader,
    Button,
    Avatar,
    OutlinedInput,
    Popper,
    Paper,
    Grow,
    ClickAwayListener
} from "@material-ui/core";
import { ThemeProvider as MuiThemeProvider } from "@material-ui/core/styles";

import { dialogTheme } from "js/react/materialThemeOverrides";
import { _ } from "js/vendor";
import { themeColors } from "js/react/sharedStyles";
import { Key } from "js/core/utilities/keys";

// Styling selected menu items (hilited when selecting with up/down arrows)
const theme = {
    overrides: {
        MuiMenuItem: {
            root: {
                "&$selected": {
                    backgroundColor: "#eeeeee"
                }
            }
        }
    }
};

const AssignSlideOutlinedInput = styled(OutlinedInput)`
    &&& {
        width: 100%;
        border-radius: 2px;
        height: 40px;

        input::placeholder {
            font-family: "Roboto", "Helvetica", "Arial", sans-serif;
            font-weight: bolder;
        }
    }
`;

const CollaboratorMenuItem = styled(MenuItem)`
    &&& {
        padding: 14px 20px 14px 14px;
        font-size: 13px;
        color: #444;

        > div {
            display: flex;
            align-items: center;
        }

        p {
            font-family: "Roboto", "Helvetica", "Arial", sans-serif;
            font-weight: 600;
            font-size: 13px;
            color: rgb(68, 68, 68);
            word-break: break-word;
            white-space: normal;
        }

        :hover {
            text-decoration: none;
            background-color: rgba(0, 0, 0, 0.04);
        }
    }
`;

const AssignedUserListItem = styled(ListItem)`
    &&& {
        display: flex;
        justify-content: center;
        flex-flow: column;
        margin-bottom: 10px;
        padding: 10px 20px 14px 14px;
        font-size: 13px;
        color: #444;

        > div {
            width: 100%;
            display: flex;
            align-items: center;
        }

        p {
            font-family: "Roboto", "Helvetica", "Arial", sans-serif;
            font-weight: 600;
            font-size: 13px;
            color: rgb(68, 68, 68);
            word-break: break-word;
            white-space: normal;
        }
    }
`;

const AssignUserListItem = styled(ListItem)`
    &&& {
        padding: 15px 20px 10px 14px;
        font-size: 13px;
        color: #444;
    }
`;

const AddCollaboratorMenuItem = styled(MenuItem)`
    &&& {
        padding: 14px 20px 14px 14px;
        font-size: 13px;
        color: #444;

        span {
            margin: 0 auto;
            text-transform: uppercase;
            letter-spacing: .6px;
            color: ${themeColors.ui_blue};
            font-weight: 600;
        }
    }
`;

const UnassignButton = styled(Button)`
    &&& {
        color: #666666;
        font-weight: bolder;
        margin: 0 auto;
    }
`;

const DropdownHeader = styled(ListSubheader)`
    &&& {
        text-transform: uppercase;
        letter-spacing: .6px;
        color: #666666;
        font-size: 12px;
        font-weight: 600;
        line-height: 45px;
    }
`;

const StyledMenuList = styled.ul`
    &&& {
        padding: 10px 0 10px 0;
    }
`;

const StyledAvatar = styled(Avatar)`
    &&& {
        margin-right: 10px;
    }
`;

const Divider = styled.hr`
    border: 1px solid #eeeeee;
`;

export class AssignCollaboratorOrTeammateDropdown extends Component {
    state = {
        selectedUserIndex: null,
        filteredCollaborators: [],
        filteredTeammates: []
    }

    componentDidMount() {
        const { anchorEl } = this.props;
        if (anchorEl) {
            window.addEventListener("keydown", this.handleKeyDown);
        }

        this.setFilteredCollaboratorsAndTeammates();
    }

    componentDidUpdate(prevProps) {
        const { teammates, collaborators, search, anchorEl } = this.props;

        if (!_.isEqual(prevProps.teammates, teammates) || !_.isEqual(prevProps.collaborators, collaborators) || prevProps.search !== search) {
            this.setFilteredCollaboratorsAndTeammates();
        }

        if (anchorEl && !prevProps.anchorEl) {
            window.addEventListener("keydown", this.handleKeyDown);
            this.setState({ selectedUserIndex: null });
        } else if (!anchorEl && prevProps.anchorEl) {
            window.removeEventListener("keydown", this.handleKeyDown);
            this.setState({ selectedUserIndex: null });
        }
    }

    componentWillUnmount() {
        const { anchorEl } = this.props;
        if (anchorEl) {
            window.removeEventListener("keydown", this.handleKeyDown);
        }
    }

    handleKeyDown = event => {
        const { onCollaboratorClick, onTeammateClick } = this.props;
        const { selectedUserIndex, filteredCollaborators, filteredTeammates } = this.state;

        const key = event.which;
        if (key === Key.UP_ARROW || key === Key.DOWN_ARROW) {
            event.preventDefault();
            event.stopPropagation();

            if (selectedUserIndex === null) {
                this.setState({ selectedUserIndex: 0 });
            } else {
                const usersCount = filteredCollaborators.length + filteredTeammates.length;
                const indexShift = key === Key.UP_ARROW ? -1 : 1;
                let newIndex = selectedUserIndex + indexShift;
                if (newIndex < 0) {
                    newIndex = usersCount + newIndex;
                }
                newIndex = newIndex % usersCount;
                this.setState({ selectedUserIndex: newIndex });
            }
        } else if (key === Key.ENTER) {
            event.preventDefault();
            event.stopPropagation();

            if (selectedUserIndex !== null) {
                if (selectedUserIndex < filteredCollaborators.length) {
                    const selectedUser = filteredCollaborators[selectedUserIndex];
                    onCollaboratorClick(selectedUser);
                } else if (selectedUserIndex < filteredCollaborators.length + filteredTeammates.length) {
                    const selectedUser = filteredTeammates[selectedUserIndex - filteredCollaborators.length];
                    onTeammateClick(selectedUser);
                }
            }
        }
    }

    setFilteredCollaboratorsAndTeammates = () => {
        const {
            maxUsersToShow,
            search,
            collaborators = [],
            teammates = []
        } = this.props;

        const filteredCollaborators = collaborators.filter(collaborator => {
            if (collaborator.displayName) {
                return collaborator.displayName.toLowerCase().includes(search) || collaborator.email.toLowerCase().includes(search);
            }
            return collaborator.email.toLowerCase().includes(search);
        }).slice(0, maxUsersToShow);

        const filteredTeammates = teammates
            // Filtering out collaborators
            .filter(teammate => !filteredCollaborators.some(collaborator => collaborator.uid === teammate.uid))
            .filter(teammate => {
                if (teammate.displayName) {
                    return teammate.displayName.toLowerCase().includes(search) || teammate.email.toLowerCase().includes(search);
                }
                return teammate.email.toLowerCase().includes(search);
            }).slice(0, maxUsersToShow - filteredCollaborators.length);

        this.setState({ filteredCollaborators, filteredTeammates });
    }

    render() {
        const {
            anchorEl,
            onClose,
            onSearchInputChange,
            onCollaboratorClick,
            onTeammateClick,
            onAddCollaboratorClick,
            onUnassignClick,
            search = "",
            assignedUser = null,
            showAssignUserInput = false
        } = this.props;

        const {
            filteredCollaborators,
            filteredTeammates,
            selectedUserIndex
        } = this.state;

        return (
            <MuiThemeProvider theme={dialogTheme}>
                <MuiThemeProvider theme={theme}>
                    <Popper
                        style={{ zIndex: 1, width: "250px" }}
                        open={!!anchorEl}
                        anchorEl={anchorEl}
                        transition
                    >
                        {({ TransitionProps }) => (
                            <Grow
                                {...TransitionProps}
                            >
                                <Paper>
                                    <ClickAwayListener onClickAway={onClose}>
                                        <StyledMenuList>
                                            {assignedUser &&
                                                <>
                                                    <AssignedUserListItem>
                                                        <div testing-data-assigned-email={assignedUser.email}>
                                                            <StyledAvatar src={assignedUser.photoURL} />
                                                            <div>
                                                                <p>{assignedUser.displayName || assignedUser.email}</p>
                                                                {assignedUser.isPending &&
                                                                    <p style={{ fontWeight: 600, color: "#a9a9a9" }}>Pending sign-up</p>
                                                                }
                                                            </div>
                                                        </div>
                                                        <UnassignButton onClick={onUnassignClick}>Unassign</UnassignButton>
                                                    </AssignedUserListItem>
                                                    <Divider />
                                                </>
                                            }
                                            {showAssignUserInput &&
                                                <>
                                                    <AssignUserListItem>
                                                        <AssignSlideOutlinedInput autoFocus={true} onChange={onSearchInputChange} value={search} placeholder={assignedUser ? "Reassign slide..." : "Assign slide..."} />
                                                    </AssignUserListItem>
                                                    {filteredTeammates.length === 0 && filteredCollaborators.length === 0 && <Divider />}
                                                </>
                                            }
                                            {filteredCollaborators.length > 0 && <DropdownHeader>Collaborators</DropdownHeader>}
                                            {filteredCollaborators.map((collaborator, idx) => (
                                                <CollaboratorMenuItem onClick={() => onCollaboratorClick(collaborator)} key={idx} selected={idx === selectedUserIndex}>
                                                    <div testing-data-collaborator-email={collaborator.email}>
                                                        <StyledAvatar src={collaborator.photoURL} />
                                                        {collaborator.isPending &&
                                                            <div>
                                                                <p>{collaborator.displayName || collaborator.email}</p>
                                                                <p style={{ fontWeight: 600, color: "#a9a9a9" }}>Pending sign-up</p>
                                                            </div>
                                                        }
                                                        {!collaborator.isPending && collaborator.teamName &&
                                                            <div>
                                                                <p>{collaborator.displayName || collaborator.email}</p>
                                                                <p style={{ fontWeight: 600, color: "#a9a9a9" }}>{collaborator.teamName}</p>
                                                            </div>
                                                        }
                                                        {!collaborator.isPending && !collaborator.teamName &&
                                                            <p>{collaborator.displayName || collaborator.email}</p>
                                                        }
                                                    </div>
                                                </CollaboratorMenuItem>
                                            ))}
                                            {filteredCollaborators.length > 0 && <Divider />}
                                            {filteredTeammates.length > 0 && <DropdownHeader>Teammates</DropdownHeader>}
                                            {filteredTeammates.map((teammate, idx) => (
                                                <CollaboratorMenuItem onClick={() => onTeammateClick(teammate)} key={idx} selected={(idx + filteredCollaborators.length) === selectedUserIndex}>
                                                    <div testing-data-teammate-email={teammate.email}>
                                                        <StyledAvatar src={teammate.photoURL} />
                                                        <p>{teammate.displayName || teammate.email}</p>
                                                    </div>
                                                </CollaboratorMenuItem>
                                            ))}
                                            {filteredTeammates.length > 0 && <Divider />}
                                            <AddCollaboratorMenuItem onClick={onAddCollaboratorClick}>
                                                <span>Add Collaborator</span>
                                            </AddCollaboratorMenuItem>
                                        </StyledMenuList>
                                    </ClickAwayListener>
                                </Paper>
                            </Grow>
                        )}
                    </Popper>
                </MuiThemeProvider>
            </MuiThemeProvider>
        );
    }
}
